import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useChangePasswordMutation } from '../users/users-api'
import { passwordChanged, setCredentials } from '../login/authSlice'
import type { RootState } from '@/store'
import useLogout from '@/hooks/useLogout'

export const MIN_PASSWORD_LENGTH = 8

const initialForm = {
  oldPassword: '',
  newPassword: '',
  newPasswordRetry: ''
}

// Screen shown right after login when the account still has the
// one-time password generated by an admin (must_change_password=true).
const ChangePasswordPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const logout = useLogout()
  const [changePassword, { isLoading, error }] = useChangePasswordMutation()
  const [form, setForm] = useState(initialForm)
  const user = useSelector((state: RootState) => state.auth.user)

  const tooShort =
    form.newPassword.length > 0 && form.newPassword.length < MIN_PASSWORD_LENGTH
  const sameAsOld =
    form.newPassword.length > 0 && form.newPassword === form.oldPassword
  const mismatch =
    form.newPasswordRetry.length > 0 &&
    form.newPasswordRetry !== form.newPassword
  const canSubmit =
    form.oldPassword.length > 0 &&
    form.newPassword.length >= MIN_PASSWORD_LENGTH &&
    form.newPassword === form.newPasswordRetry &&
    !sameAsOld

  const handleChange = ({ target }) => {
    setForm((prev) => ({ ...prev, [target.name]: target.value }))
  }

  const handleSubmit = async (event) => {
    event?.preventDefault()
    if (!canSubmit) return

    try {
      const response = await changePassword({
        old_password: form.oldPassword,
        new_password: form.newPassword
      }).unwrap()
      // backend invalidates the old tokens and returns new ones without the flag
      if (response?.token?.access_token && response?.token?.refresh_token) {
        dispatch(
          setCredentials({
            user,
            token: response.token.access_token,
            refreshToken: response.token.refresh_token,
            mustChangePassword: false
          })
        )
      } else {
        dispatch(passwordChanged())
      }
      void navigate('/', { replace: true })
    } catch {
      // error is rendered from the mutation state
    }
  }

  const errorMessage = error
    ? (error as { data?: { message?: string } })?.data?.message ||
      t('something_went_wrong!')
    : null

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        bgcolor: 'background.default'
      }}
    >
      <Card
        component="form"
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
        sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 4 }, borderRadius: 3 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('force_change_password_title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('force_change_password_hint')}
        </Typography>
        <Stack spacing={2}>
          <TextField
            name="oldPassword"
            type="password"
            label={t('old_password')}
            autoComplete="current-password"
            value={form.oldPassword}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            name="newPassword"
            type="password"
            label={t('new_password')}
            autoComplete="new-password"
            value={form.newPassword}
            onChange={handleChange}
            error={tooShort || sameAsOld}
            helperText={
              tooShort
                ? t('password_must_be_a_minimum_of_8_characters')
                : sameAsOld
                  ? t('new_password_must_differ')
                  : null
            }
            fullWidth
          />
          <TextField
            name="newPasswordRetry"
            type="password"
            label={t('repeat_the_new_password')}
            autoComplete="new-password"
            value={form.newPasswordRetry}
            onChange={handleChange}
            error={mismatch}
            helperText={mismatch ? t('the_password_must_match') : null}
            fullWidth
          />
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!canSubmit}
            loading={isLoading}
          >
            {t('change_password')}
          </Button>
          <Button color="inherit" onClick={logout}>
            {t('logout')}
          </Button>
        </Stack>
      </Card>
    </Box>
  )
}

export default ChangePasswordPage
